// Wybór płci dziecka (rodzic): od niej zależą formy w tekstach ćwiczeń („Skończył{eś|aś}”). Brak wyboru = forma męska (patrz lib/gender.ts).
// Napisy idą za przełącznikiem EN/PL, gdy ekran poda `ctx` (pierwszy profil); bez `ctx` (panel rodzica) są po polsku.
import type { Gender } from '../lib/gender.ts';
import type { Pair } from '../lib/i18n.ts';
import type { AppCtx } from './ctx.ts';
import { h } from './dom.ts';
import { txAttr, txText } from './i18n.ts';
import { liveLang } from './i18nLive.ts';

export interface GenderPicker {
  el: HTMLElement;
  get(): Gender;
}

const T = {
  title: { en: "Child's gender", pl: 'Płeć dziecka' },
  note: { en: 'The app uses it to speak to your child in the right form (boy or girl).', pl: 'Dzięki temu aplikacja zwraca się do dziecka we właściwej formie („Skończyłeś” / „Skończyłaś”).' },
  boy: { en: 'Boy', pl: 'Chłopiec' },
  girl: { en: 'Girl', pl: 'Dziewczynka' },
} as const satisfies Record<string, Pair>;

const OPTIONS: ReadonlyArray<{ value: 'm' | 'f'; label: Pair }> = [
  { value: 'm', label: T.boy },
  { value: 'f', label: T.girl },
];

export function createGenderPicker(initial: Gender, onChange: (g: Gender) => void = () => {}, ctx?: Pick<AppCtx, 'teacher' | 'account'>): GenderPicker {
  let value: Gender = initial;
  const lang = (): 'en' | 'pl' => (ctx?.teacher ? ctx.teacher.lang() : 'pl');
  const row = h('div', { class: 'gender-row', role: 'group', 'aria-label': T.title.pl, 'data-testid': 'gender-picker' });
  const paint = (): void => {
    row.replaceChildren(
      ...OPTIONS.map((o) =>
        h('button', {
          type: 'button',
          class: `gender-opt${value === o.value ? ' on' : ''}`,
          'aria-pressed': value === o.value ? 'true' : 'false',
          'data-testid': 'gender-opt',
          'data-gender': o.value,
          onclick: () => {
            value = o.value;
            paint();
            onChange(value);
          },
        }, o.label[lang()]),
      ),
    );
  };
  paint();
  const note = h('p', { class: 'muted gender-note' });
  const title = h('strong');
  const el = h('div', { class: 'gender-picker' }, title, row, note);
  if (ctx) {
    txText(ctx, title, T.title);
    txText(ctx, note, T.note);
    txAttr(ctx, row, 'aria-label', T.title);
    liveLang(ctx, el, paint); // przyciski układamy na nowo (zaznaczenie zostaje)
  } else {
    title.textContent = T.title.pl;
    note.textContent = T.note.pl;
  }
  return { el, get: () => value };
}
